import 'catalog/components/CatalogFilter/CatalogFilter.scss';

import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import MediaQuery from 'react-responsive';
import { find, debounce } from 'lodash';

import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { InputDropDown } from '@sm360/widget-commons-forms';
import {
    ShiftDigitalService,
    MEDIA_QUERIES,
    AnalyticsService,
    TRACKING_TAGS as TAGS,
    ADOBE_TAGS,
    AdobeService,
} from '@sm360/widget-commons-general';
import { Vehicle } from '@sm360/widget-commons-vehicle';

import * as Actions from 'catalog/actions/catalogActions.js';
import CatalogFilterModel from 'catalog/models/CatalogFilterModel.js';
import BodyStyles from 'catalog/components/CatalogFilter/components/BodyStyles/BodyStyles.jsx';

const { TradeInVehicleModel } = Vehicle;

/* eslint jsx-a11y/no-static-element-interactions: 0 */
const CatalogFilter = ({
    actions,
    tradeInActions,
    tradeInVehicle,
    models,
    catalogFilter,
    selectPurchaseMethod,
    resetBodyStylesFilter,
    filterMake,
    filterModel,
    hasAdobeTagging,
    isCustomBodyStyle,
}) => {
    const isInitialMountForFilter = useRef(true);
    const isInitialMountForKeyword = useRef(true);

    const searchAnalytics = (value) => {
        AnalyticsService.ga4Trigger(TAGS.SEARCH_BAR(value));
        AnalyticsService.ga4Trigger(TAGS.LAYER_NAVIGATION__FILTERS(WindowProxy.location().pathname, 'searchbar'));
    };
    const debounceSearchAnalytics = useCallback(debounce(searchAnalytics, 5000), []);

    const tradeInValueAnalytics = (value) => AnalyticsService.ga4Trigger(TAGS.TRADE_IN_VALUE(value));
    const debounceTradeInValueAnalytics = useCallback(debounce(tradeInValueAnalytics, 5000), []);

    const oweOnTradeAnalytics = (value) => AnalyticsService.ga4Trigger(TAGS.OWE_ON_TRADE(value));
    const debounceOweOnTradeAnalytics = useCallback(debounce(oweOnTradeAnalytics, 5000), []);

    /*
    * Need to send Shift digital events only once when load the filters
    * Send Shift digital event on interaction with filter and keyword search
    */
    useEffect(
        () => {
            if (isInitialMountForFilter.current) {
                isInitialMountForFilter.current = false;
            } else {
                ShiftDigitalService.updateCatalogEvent(catalogFilter);
            }
        },
        [catalogFilter.bodyStylesFilterCodes, catalogFilter.bodyStylesFilterAll, catalogFilter.preferredMake]
    );

    useEffect(
        () => {
            if (isInitialMountForKeyword.current) {
                isInitialMountForKeyword.current = false;
            } else {
                ShiftDigitalService.updateCatalogEvent(catalogFilter);
            }
        },
        [catalogFilter.modelSearchString]
    );

    const hasMultipleMakes =
        catalogFilter.getMakeDropDownValues().length > 2 ||
        (catalogFilter.getMakeDropDownValues().length === 2 &&
            !find(catalogFilter.getMakeDropDownValues(), ['value', 'ALL']));

    const handleMakesChange = (selectedOption, name) => {
        filterMake(selectedOption, name);
        AnalyticsService.ga4Trigger(TAGS.MAKES(selectedOption.label));
        AnalyticsService.ga4Trigger(TAGS.LAYER_NAVIGATION__FILTERS(WindowProxy.location().pathname, 'makeIds'));
    };

    const makesInputDropDown = hasMultipleMakes && (
        <div className="catalog-filter-make-select">
            <InputDropDown
                name="make"
                value={catalogFilter.getMakeValue()}
                placeholder={LocaleService.t('FILTER.MAKE')}
                options={catalogFilter.getMakeDropDownValues()}
                valueChange={handleMakesChange}
                classNames={['input-dropdown']}
            />
        </div>
    );

    const handleTradeInValueChange = (value) => {
        const storage = WindowProxy.localStorage();
        const valueFromLocalStorage = storage && parseInt(storage.getItem('tradeIn'), 10);
        if (value && value !== valueFromLocalStorage) debounceTradeInValueAnalytics(value);
        actions.setTradeIn(value);
    };

    const handleOweOnTradeChange = (value) => {
        const storage = WindowProxy.localStorage();
        const valueFromLocalStorage = storage && parseInt(storage.getItem('lien'), 10);
        if (value && value !== valueFromLocalStorage) debounceOweOnTradeAnalytics(value);
        actions.setLien(value);
    };

    const handlePurchaseMethodSelection = (selectedPurchaseMethod) => {
        AnalyticsService.gaTrigger(TAGS.SORT_BY(WindowProxy.location().pathname, selectedPurchaseMethod.label));
        if (hasAdobeTagging) {
            AdobeService.eventTrigger(
                ADOBE_TAGS.CATALOG_PAGE_SORT_LIST(selectedPurchaseMethod?.label),
                'global-onclick'
            );
        }
        selectPurchaseMethod(selectedPurchaseMethod);
    };

    return (
        <section className="section-catalog-filter">
            <div className="catalog-filter-search">
                <div className="catalog-filter-input-search">
                    <input
                        type="text"
                        placeholder={LocaleService.t('FILTER.IM_LOOKING_FOR')}
                        onChange={(event) => {
                            filterModel(event.target.value);
                            if (event.target.value) debounceSearchAnalytics(event.target.value);
                        }}
                    />
                </div>

                {makesInputDropDown}
                <MediaQuery minWidth={MEDIA_QUERIES.TABLET.MIN}>
                    <div className="catalog-filter-selection clearfix">
                        <BodyStyles
                            bodyStyles={catalogFilter.bodyStyles.toArray()}
                            resetBodyStylesFilter={resetBodyStylesFilter}
                            models={models}
                            hasAdobeTagging={hasAdobeTagging}
                            isCustomBodyStyle={isCustomBodyStyle}
                        />
                    </div>
                </MediaQuery>
            </div>
            <MediaQuery maxWidth={MEDIA_QUERIES.MOBILE.MAX}>
                <div className="catalog-filter-selection clearfix">
                    <BodyStyles
                        bodyStyles={catalogFilter.bodyStyles.toArray()}
                        resetBodyStylesFilter={resetBodyStylesFilter}
                        models={models}
                        hasAdobeTagging={hasAdobeTagging}
                        isCustomBodyStyle={isCustomBodyStyle}
                    />
                </div>
            </MediaQuery>
            <div className="catalog-filter__trade-in-section">
                <div className="catalog-filter__trade-in-wrapper">
                    <p className="trade-in-info sr-text is-light">{LocaleService.t('FILTER.TRADE_IN')}</p>
                    <Vehicle.TradeInValue
                        tradeInVehicle={tradeInVehicle}
                        setTradeIn={handleTradeInValueChange}
                        setLien={handleOweOnTradeChange}
                        saveTradeInVehicle={tradeInActions.saveTradeInVehicle}
                        initialTargetPayment={0}
                    />
                </div>
                <InputDropDown
                    name="purchaseMethodSort"
                    value={catalogFilter.getPurcheMethodOrderValue()}
                    options={catalogFilter.purchaseMethodOrderValues}
                    placeholder={LocaleService.t('FILTER.PURCHASE_METHOD_ORDER.TITLE')}
                    valueChange={handlePurchaseMethodSelection}
                    classNames={['input-dropdown not-empty order-option']}
                />
            </div>
        </section>
    );
};

CatalogFilter.propTypes = {
    actions: PropTypes.shape({
        setLien: PropTypes.func.isRequired,
        setTradeIn: PropTypes.func.isRequired,
    }).isRequired,
    tradeInActions: PropTypes.shape({
        saveTradeInVehicle: PropTypes.func.isRequired,
    }).isRequired,
    catalogFilter: PropTypes.instanceOf(CatalogFilterModel).isRequired,
    selectPurchaseMethod: PropTypes.func.isRequired,
    resetBodyStylesFilter: PropTypes.func.isRequired,
    filterMake: PropTypes.func.isRequired,
    filterModel: PropTypes.func.isRequired,
    tradeInVehicle: PropTypes.instanceOf(TradeInVehicleModel),
    models: PropTypes.instanceOf(Immutable.List),
    hasAdobeTagging: PropTypes.bool.isRequired,
    isCustomBodyStyle: PropTypes.bool.isRequired,
};

CatalogFilter.displayName = 'CatalogFilter';

function mapStateToProps(state) {
    return {
        tradeInVehicle: state.tradeInVehicle,
        models: state.manufacturer.models,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(Actions, dispatch),
        tradeInActions: bindActionCreators(Vehicle.TradeInVehicleActions, dispatch),
    };
}

export { CatalogFilter };
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(CatalogFilter);
